import { filter, map, sortBy, uniq } from "lodash-es";
import { observer } from "mobx-react-lite";

import { MDSIconKind } from "@/mds";
import { AgentFilterSearchField } from "@/pages/agent/components/agent-filter-search-field";
import { AgentFilterSelect } from "@/pages/agent/components/agent-filter-select";
import { AgentPageFilterBar, AgentPageLayout } from "@/pages/agent/components/agent-page-layout";
import { AgentSurfaceNavigationActiveTab } from "@/pages/agent/components/agent-surface-navigation";
import {
  buildProjectSortFilterItems,
  buildProjectStatusFilterItems,
} from "@/pages/agent/projects/agent-project-filter-items";
import {
  buildProjectCards,
  DEFAULT_PROJECT_STATUS_FILTER_VALUES,
  getSortedProjectCards,
  ProjectSectionKind,
} from "@/pages/agent/projects/agent-projects-page.utils";
import { AgentProjectsSection } from "@/pages/agent/projects/agent-projects-section";
import {
  ALL_PROJECT_STATUSES_FILTER,
  DEFAULT_PROJECT_SORT,
  useAgentProjectsSearchParams,
} from "@/pages/agent/projects/use-agent-projects-search-params";
import { showNotImplementedAlert } from "@/pages/agent/utils/agent-not-implemented";
import { formatStatusLabel, normalizeStatusValue } from "@/pages/agent/utils/agent-status.utils";
import { useAppStore } from "@/store";

export const AgentProjectsPage = observer(() => {
  const { store } = useAppStore();
  const { statusFilter, searchQuery, sortFilter, updateProjectFilters } =
    useAgentProjectsSearchParams();

  const projectCards = buildProjectCards({
    projects: store.assistantProjects.all,
    tasks: store.assistantTasks.all,
    followUps: store.assistantFollowUps.all,
  });

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const filteredProjectCards = filter(projectCards, projectCard => {
    const matchesStatus =
      statusFilter === ALL_PROJECT_STATUSES_FILTER ||
      normalizeStatusValue({ status: projectCard.project.status }) === statusFilter;
    const matchesSearch =
      !normalizedSearchQuery || projectCard.searchableText.includes(normalizedSearchQuery);

    return matchesStatus && matchesSearch;
  });

  const activeProjectCards = getSortedProjectCards({
    projectCards: filter(filteredProjectCards, projectCard => !projectCard.isClosed),
    sortKey: sortFilter,
  });
  const completedProjectCards = getSortedProjectCards({
    projectCards: filter(filteredProjectCards, projectCard => projectCard.isClosed),
    sortKey: sortFilter,
  });

  const sortFilterItems = buildProjectSortFilterItems();

  const statusFilterValues = sortBy(
    uniq([
      ...DEFAULT_PROJECT_STATUS_FILTER_VALUES,
      ...map(projectCards, projectCard =>
        normalizeStatusValue({ status: projectCard.project.status })
      ),
    ]),
    status => formatStatusLabel({ status })
  );
  const statusFilterItems = buildProjectStatusFilterItems({
    statusFilter,
    statusFilterValues,
  });

  const openProject = ({ projectId }: { projectId: string }) => {
    store.agentOverlay.openProject({ projectId });
  };

  return (
    <AgentPageLayout activeTab={AgentSurfaceNavigationActiveTab.Projects}>
      <AgentPageFilterBar>
        <AgentFilterSelect
          label="Sort"
          selectedKey={sortFilter}
          defaultKey={DEFAULT_PROJECT_SORT}
          items={sortFilterItems}
          leadingIcon={MDSIconKind.SortAlt}
          alwaysActive
          onSelectionChange={({ key }) => {
            updateProjectFilters({ nextSortFilter: key });
          }}
          onClear={() => {
            updateProjectFilters({ nextSortFilter: DEFAULT_PROJECT_SORT });
          }}
        />

        <AgentFilterSelect
          label="Status"
          selectedKey={statusFilter}
          defaultKey={ALL_PROJECT_STATUSES_FILTER}
          items={statusFilterItems}
          leadingIcon={MDSIconKind.Circle}
          onSelectionChange={({ key }) => {
            updateProjectFilters({ nextStatusFilter: key });
          }}
          onClear={() => {
            updateProjectFilters({ nextStatusFilter: ALL_PROJECT_STATUSES_FILTER });
          }}
        />

        <AgentFilterSearchField
          ariaLabel="Filter projects by name"
          placeholder="Filter by name"
          value={searchQuery}
          onChange={value => {
            updateProjectFilters({ nextSearchQuery: value });
          }}
        />
      </AgentPageFilterBar>

      <AgentProjectsSection
        title="Active projects"
        kind={ProjectSectionKind.Active}
        projectCards={activeProjectCards}
        onOpenActivities={() => {
          store.agentOverlay.openActivities();
        }}
        onOpenProject={openProject}
        onOpenTasks={() => {
          store.agentOverlay.openTasks();
        }}
        onOpenNextUp={showNotImplementedAlert}
        onCommentProject={showNotImplementedAlert}
      />

      <AgentProjectsSection
        title="Completed projects"
        kind={ProjectSectionKind.Completed}
        projectCards={completedProjectCards}
        onOpenActivities={() => {
          store.agentOverlay.openActivities();
        }}
        onOpenProject={openProject}
        onOpenTasks={() => {
          store.agentOverlay.openTasks();
        }}
        onOpenNextUp={showNotImplementedAlert}
        onCommentProject={showNotImplementedAlert}
      />
    </AgentPageLayout>
  );
});
